import {
  convertTimezoneToLocal,
  format,
  formatInTimeZone,
  isFuture,
  isSameDay,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInYears,
  getTimezoneData,
} from '@chilipiper/date-time'
import { DEFAULT_LOCALE } from '@chilipiper/locale/src/new'

export type TimeSlot = {
  timezoneSlot: Date
  defaultSlot: string
}

export type SlotLabel = {
  hour: string
  period: string
}

export type MeetingDateRange = {
  start: string
  end: string
}

export type SlotsByDayResult = {
  availableDays: Record<string, boolean>
  slotsByDay: Record<string, string[]>
  getSlotsForDay: (day: string) => string[]
}

export type RelativeTimeOptions = Intl.RelativeTimeFormatOptions & {
  underMinuteText?: string
}

const TIME_UNITS = {
  MINUTES_PER_HOUR: 60,
  HOURS_PER_DAY: 24,
  DAYS_PER_MONTH: 30,
  MONTHS_PER_YEAR: 12,
}

const DATE_FORMATS = {
  TIME_12H: 'p',
  TIME_24H: 'HH:mm',
  DATE_FULL: 'EEEE, MMM d,',
  TIME_RANGE: 'h:mm a',
  DATE_ISO: 'yyyy-MM-dd',
}

const DEFAULT_RELATIVE_TIME_OPTIONS: Intl.RelativeTimeFormatOptions = {
  numeric: 'auto',
}

const DEFAULT_UNDER_MINUTE_TEXT = 'Now'
const DEFAULT_ERROR_DISPLAY = '-'

const toSafeDate = (date: Date | string | number): Date => {
  const result = new Date(date)
  if (isNaN(result.getTime())) {
    throw new Error(`Invalid date: ${date.toString()}`)
  }
  return result
}

const validateRequired = (params: Record<string, unknown>, paramNames: string[]): void => {
  const missing = paramNames.filter(name => !params[name])
  if (missing.length > 0) {
    throw new Error(`Missing required parameters: ${missing.join(', ')}`)
  }
}

const mapSlotsToTimezone = (slots: string[], timezone: string): TimeSlot[] => {
  validateRequired({ timezone }, ['timezone'])

  return slots.map((slot: string) => ({
    timezoneSlot: convertTimezoneToLocal(slot, timezone),
    defaultSlot: slot,
  }))
}

const filterSlotsByDay = (timeSlots: TimeSlot[], selectedDay: Date): TimeSlot[] => {
  validateRequired({ selectedDay }, ['selectedDay'])

  return timeSlots.filter(slot => isSameDay(slot.timezoneSlot, selectedDay))
}

export const mapSelectedDaySlots = (
  selectedDay: Date,
  timezone: string,
  slots: string[] = []
): TimeSlot[] => {
  validateRequired({ selectedDay, timezone }, ['selectedDay', 'timezone'])

  const mappedSlots = mapSlotsToTimezone(slots, timezone)
  return filterSlotsByDay(mappedSlots, selectedDay)
}

export const hasAvailableSlots = (slots: string[], timezone: string): boolean => {
  validateRequired({ timezone }, ['timezone'])

  return slots.length > 0 && slots.some(slot => isFuture(convertTimezoneToLocal(slot, timezone)))
}

export const getSlotsForDay = (day: string, timezone: string, slots: string[] = []): string[] => {
  validateRequired({ day, timezone }, ['day', 'timezone'])

  if (!slots.length) {
    return []
  }

  return slots.filter(slot => {
    try {
      return formatInTimeZone(toSafeDate(slot), timezone, DATE_FORMATS.DATE_ISO) === day
    } catch {
      return false
    }
  })
}

export const getAvailableDaysBySlots = (
  slots: string[] = [],
  timezone: string
): Record<string, boolean> => {
  validateRequired({ timezone }, ['timezone'])

  return slots.reduce(
    (acc, slot) => {
      try {
        const day = formatInTimeZone(toSafeDate(slot), timezone, DATE_FORMATS.DATE_ISO)
        acc[day] = true
      } catch {
        // Skip invalid slots
      }
      return acc
    },
    {} as Record<string, boolean>
  )
}

export const getSlotsByDay = (slots: string[] = [], timezone: string): SlotsByDayResult => {
  validateRequired({ timezone }, ['timezone'])

  const availableDays = getAvailableDaysBySlots(slots, timezone)

  const slotsByDay = Object.keys(availableDays).reduce(
    (acc, day) => {
      acc[day] = getSlotsForDay(day, timezone, slots)
      return acc
    },
    {} as Record<string, string[]>
  )

  return {
    availableDays,
    slotsByDay,
    getSlotsForDay: (day: string) => getSlotsForDay(day, timezone, slots),
  }
}

export const getSlotLabel = (
  startTime: Date | number,
  locale: string,
  timezone?: string
): SlotLabel => {
  validateRequired({ startTime, locale }, ['startTime', 'locale'])

  try {
    const timeToFormat = timezone ? convertTimezoneToLocal(startTime, timezone) : startTime

    const isDefaultLocale = locale === DEFAULT_LOCALE
    const formatPattern = isDefaultLocale ? DATE_FORMATS.TIME_12H : DATE_FORMATS.TIME_24H
    const formattedTime = format(timeToFormat, formatPattern, { locale })

    const [hour, period = ''] = formattedTime.split(' ')

    return { hour, period }
  } catch (error) {
    console.error('Error formatting slot label:', error)
    return { hour: DEFAULT_ERROR_DISPLAY, period: '' }
  }
}

const getRelativeTimeUnit = (now: Date, then: Date) => {
  const minutesDiff = differenceInMinutes(now, then)
  const hoursDiff = differenceInHours(now, then)
  const daysDiff = differenceInDays(now, then)
  const monthsDiff = differenceInMonths(now, then)
  const yearsDiff = differenceInYears(now, then)

  if (minutesDiff < 1) {
    return {
      value: 0,
      unit: 'minute',
    }
  }
  if (minutesDiff < TIME_UNITS.MINUTES_PER_HOUR) {
    return {
      value: -minutesDiff,
      unit: 'minute',
    }
  }

  if (hoursDiff < TIME_UNITS.HOURS_PER_DAY) {
    return {
      value: -hoursDiff,
      unit: 'hour',
    }
  }
  if (daysDiff <= TIME_UNITS.DAYS_PER_MONTH) {
    return {
      value: -daysDiff,
      unit: 'day',
    }
  }
  if (monthsDiff < TIME_UNITS.MONTHS_PER_YEAR) {
    return {
      value: -monthsDiff,
      unit: 'month',
    }
  }
  return {
    value: -yearsDiff,
    unit: 'year',
  }
}

export const getRelativeTime = (
  timestamp: string | undefined,
  options: RelativeTimeOptions = {}
): string => {
  if (!timestamp) {
    return DEFAULT_ERROR_DISPLAY
  }

  try {
    const { underMinuteText = DEFAULT_UNDER_MINUTE_TEXT, ...formatOptions } = options
    const relativeTimeFormatter = new Intl.RelativeTimeFormat('en-US', {
      ...DEFAULT_RELATIVE_TIME_OPTIONS,
      ...formatOptions,
    })

    const now = new Date()
    const then = toSafeDate(timestamp)
    const { value, unit } = getRelativeTimeUnit(now, then)

    if (value === 0) {
      return underMinuteText
    }

    const formatted = relativeTimeFormatter.format(value, unit as Intl.RelativeTimeFormatUnit)

    // Abbreviate minutes for brevity
    return unit === 'minute' ? formatted.replace(/minutes?/, 'min') : formatted
  } catch (error) {
    console.error('Error in getRelativeTime:', error)
    return DEFAULT_ERROR_DISPLAY
  }
}

export const formatMeetingDate = (date: MeetingDateRange, timezone: string): string => {
  validateRequired({ date, timezone }, ['date', 'timezone'])

  try {
    return formatInTimeZone(toSafeDate(date.start), timezone, DATE_FORMATS.DATE_FULL)
  } catch (error) {
    console.error('Error formatting meeting date:', error)
    return DEFAULT_ERROR_DISPLAY
  }
}

export const formatMeetingTimeRange = (date: MeetingDateRange, timezone: string): string => {
  validateRequired({ date, timezone }, ['date', 'timezone'])

  try {
    const startTime = formatInTimeZone(toSafeDate(date.start), timezone, DATE_FORMATS.TIME_RANGE)
    const endTime = formatInTimeZone(toSafeDate(date.end), timezone, DATE_FORMATS.TIME_RANGE)
    return `${startTime} - ${endTime}`
  } catch (error) {
    console.error('Error formatting meeting time range:', error)
    return DEFAULT_ERROR_DISPLAY
  }
}

export const getMeetingTimezoneAbbreviation = (
  date: MeetingDateRange,
  timezone: string
): string => {
  validateRequired({ date, timezone }, ['date', 'timezone'])

  try {
    return getTimezoneData(timezone, toSafeDate(date.start))?.abbreviation ?? ''
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error)
    return ''
  }
}

export const formatCompleteMeetingDateTime = (date: MeetingDateRange, timezone: string): string => {
  validateRequired({ date, timezone }, ['date', 'timezone'])

  const dateStr = formatMeetingDate(date, timezone)
  const timeStr = formatMeetingTimeRange(date, timezone)
  const abbr = getMeetingTimezoneAbbreviation(date, timezone)

  return `${dateStr} ${timeStr} ${abbr}`.trim()
}

export const formatDateToYMD = (date: Date | string | number, timezone: string): string => {
  validateRequired({ date, timezone }, ['date', 'timezone'])

  try {
    return formatInTimeZone(toSafeDate(date), timezone, DATE_FORMATS.DATE_ISO)
  } catch (error) {
    console.error('Error formatting date to YMD:', error)
    return DEFAULT_ERROR_DISPLAY
  }
}

export const getCurrentDateInTimezone = (timezone: string): string => {
  validateRequired({ timezone }, ['timezone'])

  return formatDateToYMD(Date.now(), timezone)
}
